import { useState, useEffect, useCallback } from 'react';
import { getApiBase } from '../utils/getApiBase';

/**
 * Hook do zarządzania subskrypcją Web Push
 */
/** Kanoniczny identyfikator użytkownika (spójny z backendem i panelem powiadomień). */
function getCanonicalUserId(user) {
    if (!user) return '';
    return user.login || user.id || user.name || '';
}

export function usePushSubscription(user) {
    const [subscription, setSubscription] = useState(null);
    const [isSupported, setIsSupported] = useState(false);
    const [permission, setPermission] = useState('default');
    const [isSubscribing, setIsSubscribing] = useState(false);
    const [syncError, setSyncError] = useState(null);

    // Sprawdź czy Web Push jest wspierane
    useEffect(() => {
        if (typeof window === 'undefined') return;
        
        const checkSupport = () => {
            const supported = 
                'serviceWorker' in navigator &&
                'PushManager' in window &&
                'Notification' in window;
            setIsSupported(supported);
            
            if (supported && 'Notification' in window) {
                setPermission(Notification.permission);
            }
        };
        
        checkSupport();
    }, []);

    // Zgoda na powiadomienia: raz przy starcie i przy zmianie z Permissions API.
    // Bez setInterval — otwarta karta nie odpytuje niczego w pętli.
    useEffect(() => {
        if (typeof window === 'undefined' || !('Notification' in window)) return;

        const toNotificationPermission = (state) => (state === 'prompt' ? 'default' : state);
        setPermission(toNotificationPermission(Notification.permission));

        let permStatus = null;
        const onChange = () => {
            if (!permStatus) return;
            setPermission(toNotificationPermission(permStatus.state));
        };

        if (navigator.permissions?.query) {
            navigator.permissions.query({ name: 'notifications' }).then((status) => {
                permStatus = status;
                setPermission(toNotificationPermission(status.state));
                if (typeof status.addEventListener === 'function') {
                    status.addEventListener('change', onChange);
                } else {
                    status.onchange = onChange;
                }
            }).catch(() => {});
        }

        return () => {
            if (!permStatus) return;
            if (typeof permStatus.removeEventListener === 'function') {
                permStatus.removeEventListener('change', onChange);
            }
            permStatus.onchange = null;
        };
    }, []);

    // Funkcja pomocnicza do konwersji ArrayBuffer na base64 (używana w syncSubscriptionWithServer)
    const arrayBufferToBase64 = (buffer) => {
        if (!buffer) return null;
        const bytes = new Uint8Array(buffer);
        let binary = '';
        for (let i = 0; i < bytes.byteLength; i++) {
            binary += String.fromCharCode(bytes[i]);
        }
        return btoa(binary);
    };

    // Synchronizuj subskrypcję z serwerem (zdefiniowane przed useEffect, które jej używa)
    const syncSubscriptionWithServer = useCallback(async (sub, options = {}) => {
        const { retryOnce = false } = options;
        if (!user || !sub) {
            console.warn('[usePushSubscription] ⚠️ Brak user lub sub - pomijam synchronizację');
            setSyncError(null);
            return;
        }

        const userId = getCanonicalUserId(user);
        if (!userId) {
            console.warn('[usePushSubscription] ⚠️ Brak userId - pomijam synchronizację subskrypcji');
            setSyncError(null);
            return;
        }

        console.log('[usePushSubscription] 📤 Synchronizuję subskrypcję z serwerem dla:', userId);
        setSyncError(null);

        const doSync = async () => {
            const p256dhKey = sub.getKey('p256dh');
            const authKey = sub.getKey('auth');

            if (!p256dhKey || !authKey) {
                console.error('[usePushSubscription] Brak kluczy w subskrypcji');
                setSyncError('Brak kluczy subskrypcji.');
                return;
            }

            const response = await fetch(`${getApiBase()}/api/savePushSubscription`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    userId,
                    userName: user.name || user.login || user.id || '',
                    userLogin: user.login || '',
                    subscription: {
                        endpoint: sub.endpoint,
                        keys: {
                            p256dh: arrayBufferToBase64(p256dhKey),
                            auth: arrayBufferToBase64(authKey)
                        }
                    }
                })
            });

            if (!response.ok) {
                const errorText = await response.text();
                console.error(`[usePushSubscription] ❌ Błąd HTTP ${response.status}:`, errorText);
                throw new Error(`HTTP ${response.status}: ${errorText.slice(0, 100)}`);
            }

            const result = await response.json();
            console.log('[usePushSubscription] ✅ Subskrypcja zsynchronizowana z serwerem:', result);
            setSyncError(null);
        };

        try {
            await doSync();
        } catch (error) {
            console.error('[usePushSubscription] ❌ Błąd synchronizacji subskrypcji:', error);
            const message = 'Nie udało się zapisać powiadomień na serwerze. Sprawdź połączenie i spróbuj ponownie.';
            setSyncError(message);
            if (retryOnce) {
                await new Promise(resolve => setTimeout(resolve, 2000));
                try {
                    await doSync();
                    setSyncError(null);
                } catch (retryErr) {
                    console.error('[usePushSubscription] ❌ Ponowna próba synchronizacji nie powiodła się:', retryErr);
                    setSyncError(message);
                }
            }
        }
    }, [user]);

    // Pobierz istniejącą subskrypcję (po syncSubscriptionWithServer, żeby uniknąć "before initialization")
    useEffect(() => {
        if (!isSupported || !user) return;

        const getSubscription = async () => {
            try {
                const registration = await navigator.serviceWorker.ready;
                const sub = await registration.pushManager.getSubscription();
                setSubscription(sub);

                if (sub) {
                    await syncSubscriptionWithServer(sub);
                }
            } catch (error) {
                console.error('[usePushSubscription] Błąd pobierania subskrypcji:', error);
            }
        };

        getSubscription();
    }, [isSupported, user, syncSubscriptionWithServer]);

    // Poproś o zgodę i zasubskrybuj
    const subscribe = useCallback(async () => {
        if (!isSupported || !user) {
            console.warn('[usePushSubscription] Web Push nie jest wspierane lub użytkownik nie jest zalogowany');
            return false;
        }

        if (permission === 'denied') {
            console.warn('[usePushSubscription] Użytkownik odrzucił powiadomienia');
            return false;
        }

        setIsSubscribing(true);

        try {
            // Poproś o zgodę
            // Na Android requestPermission musi być wywołane w kontekście user gesture
            if (permission === 'default') {
                let permissionResult;
                try {
                    permissionResult = await Notification.requestPermission();
                } catch (error) {
                    console.error('[usePushSubscription] Błąd podczas żądania uprawnień:', error);
                    // Na niektórych przeglądarkach (np. Android Chrome) requestPermission może wymagać user gesture
                    // W takim przypadku zwróć false i pozwól użytkownikowi kliknąć ponownie
                    setIsSubscribing(false);
                    return false;
                }
                setPermission(permissionResult);
                
                if (permissionResult !== 'granted') {
                    setIsSubscribing(false);
                    return false;
                }
            }

            // Pobierz VAPID public key z serwera
            const vapidResponse = await fetch(`${getApiBase()}/api/getVapidPublicKey`);
            if (!vapidResponse.ok) {
                throw new Error('Nie udało się pobrać VAPID public key');
            }
            const { publicKey, configured } = await vapidResponse.json();
            
            // Jeśli VAPID keys nie są skonfigurowane, ale permission jest 'granted',
            // zwróć true (powiadomienia są włączone, ale bez push - będą zapisywane tylko do bazy)
            if (!publicKey || !configured || publicKey.trim() === '') {
                console.warn('[usePushSubscription] ⚠️ VAPID keys nie są skonfigurowane — brak prawdziwego push');
                setIsSubscribing(false);
                return false;
            }

            // Waliduj długość klucza przed konwersją (VAPID public key powinien mieć ~87 znaków w base64)
            if (publicKey.length < 20) {
                console.error('[usePushSubscription] ❌ VAPID public key jest za krótki:', publicKey.length);
                setIsSubscribing(false);
                return false;
            }

            // Konwertuj VAPID public key z base64 URL-safe na ArrayBuffer
            let applicationServerKey;
            try {
                applicationServerKey = urlBase64ToUint8Array(publicKey);
            } catch (error) {
                console.error('[usePushSubscription] ❌ Błąd konwersji VAPID public key:', error);
                setIsSubscribing(false);
                return false;
            }

            // Zasubskrybuj
            // Na Android service worker może potrzebować więcej czasu na gotowość
            let registration;
            try {
                registration = await navigator.serviceWorker.ready;
            } catch (error) {
                console.error('[usePushSubscription] Błąd podczas oczekiwania na service worker:', error);
                // Spróbuj ponownie po krótkim opóźnieniu
                await new Promise(resolve => setTimeout(resolve, 500));
                registration = await navigator.serviceWorker.ready;
            }
            
            const sub = await registration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: applicationServerKey
            });

            setSubscription(sub);

            // Zsynchronizuj z serwerem (jedna automatyczna ponowna próba po 2 s przy błędzie sieci)
            await syncSubscriptionWithServer(sub, { retryOnce: true });
            
            console.log('[usePushSubscription] ✅ Subskrypcja utworzona i zsynchronizowana:', {
                endpoint: sub.endpoint.substring(0, 50) + '...',
                hasKeys: !!(sub.getKey('p256dh') && sub.getKey('auth'))
            });

            setIsSubscribing(false);
            return true;
        } catch (error) {
            console.error('[usePushSubscription] ❌ Błąd subskrypcji:', error);
            console.error('[usePushSubscription] ❌ Szczegóły błędu:', {
                message: error.message,
                stack: error.stack,
                name: error.name
            });
            setIsSubscribing(false);
            return false;
        }
    }, [isSupported, user, permission, syncSubscriptionWithServer]);

    // Anuluj subskrypcję
    const unsubscribe = useCallback(async () => {
        if (!subscription) return false;

        try {
            await subscription.unsubscribe();
            setSubscription(null);

            // Usuń z serwera
            if (user) {
                await fetch(`${getApiBase()}/api/removePushSubscription`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        userId: getCanonicalUserId(user),
                        endpoint: subscription.endpoint
                    })
                });
            }

            return true;
        } catch (error) {
            console.error('[usePushSubscription] Błąd anulowania subskrypcji:', error);
            return false;
        }
    }, [subscription, user]);

    // Powiadomienia są „włączone”, gdy mamy aktywną subskrypcję (żeby „Wyłącz” faktycznie przełączało stan)
    const isSubscribed = Boolean(subscription);

    return {
        subscription,
        isSupported,
        permission,
        isSubscribed,
        isSubscribing,
        syncError,
        subscribe,
        unsubscribe,
    };
}

// Funkcja pomocnicza do konwersji VAPID public key
function urlBase64ToUint8Array(base64String) {
    if (!base64String || typeof base64String !== 'string' || base64String.trim() === '') {
        throw new Error('VAPID public key jest pusty lub nieprawidłowy');
    }
    
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
        .replace(/\-/g, '+')
        .replace(/_/g, '/');

    let rawData;
    try {
        rawData = window.atob(base64);
    } catch (error) {
        throw new Error(`Błąd dekodowania base64: ${error.message}`);
    }
    
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
        outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
}

